"use client";

import { useState } from "react";
import { Menu } from "lucide-react";
import { useTranslations } from "next-intl";
import { Link, usePathname } from "@/i18n/navigation";
import { routes } from "@/lib/routes";
import { Wordmark } from "@/components/layout/wordmark";
import { LanguageSwitch } from "@/components/layout/language-switch";
import { Button, buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Sheet, SheetClose, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

export function Nav() {
  const t = useTranslations("nav");
  const pathname = usePathname();
  const [open, setOpen] = useState(false);

  const links = [
    { href: routes.home, label: t("home") },
    { href: routes.ristorante, label: t("ristorante") },
    { href: routes.team, label: t("team") },
    { href: routes.menu, label: t("menu") },
    { href: routes.cantina, label: t("cantina") },
    { href: routes.galleria, label: t("galleria") },
    { href: routes.contatti, label: t("contatti") },
  ];

  return (
    <header className="sticky top-0 z-40 border-b border-pietra-light/50 bg-avorio">
      <div className="mx-auto flex items-center justify-between px-5 py-3 md:px-8 lg:px-14">
        <Link href={routes.home} className="shrink-0">
          <Wordmark priority />
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Navigazione principale">
          {links.map((link) => {
            const isActive = pathname === link.href;
            return (
              <Link
                key={link.href}
                href={link.href}
                className={`border-b pb-1 font-body text-xs font-medium tracking-[0.18em] uppercase transition-colors ${
                  isActive
                    ? "border-rosso-pozzo text-rosso-pozzo"
                    : "border-transparent text-inchiostro hover:text-rosso-pozzo"
                }`}
              >
                {link.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-5">
          <LanguageSwitch className="hidden lg:flex" />
          <Button nativeButton={false} className="hidden sm:inline-flex" render={<Link href={routes.prenota} />}>
            {t("prenota")}
          </Button>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger
              render={
                <button
                  type="button"
                  aria-label="Apri il menu"
                  className="flex size-9 items-center justify-center text-inchiostro lg:hidden"
                />
              }
            >
              <Menu size={22} />
            </SheetTrigger>
            <SheetContent side="right" className="bg-avorio">
              <SheetTitle className="sr-only">Navigazione</SheetTitle>
              <nav className="mt-10 flex flex-col gap-6 px-6" aria-label="Navigazione mobile">
                {links.map((link) => (
                  <SheetClose
                    key={link.href}
                    nativeButton={false}
                    render={<Link href={link.href} className="font-display text-2xl" />}
                  >
                    {link.label}
                  </SheetClose>
                ))}
                <SheetClose
                  nativeButton={false}
                  render={
                    <Link href={routes.prenota} className={cn(buttonVariants({}), "mt-4 justify-center")} />
                  }
                >
                  {t("prenota")}
                </SheetClose>

                <LanguageSwitch className="mt-6 border-t border-pietra-light/50 pt-6 text-base [&_svg:first-child]:h-4 [&_svg:first-child]:w-6" />
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
